import { langGraphClient, getLangGraphClient, LangGraphClient } from '@/lib/langgraph-client-v2';
import type {
  StreamCallbacks,
  StreamController,
  ThreadMetadata,
  LangGraphMessage,
  LangGraphMessageInput,
} from '@/types/langgraph';
import type { CurrentTeam, TeamLeader } from '@/types/onboarding.types';
import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';

/**
 * Input for sendMessageStream - supports both string (backward compatible) and message array
 */
export type SendMessageInput = string | LangGraphMessageInput[];

/**
 * Thread history data containing both messages and UI events
 */
export interface ThreadHistoryData {
  messages: LangGraphMessage[];
  uiEvents: any[];
  currentTeam: CurrentTeam | null;
  teamLeader: TeamLeader | null;
}

/**
 * Chat Assistant Service
 *
 * Service layer for chat operations using LangGraph Client
 * Uses error tuple pattern: [Error | null, Data | null]
 */
export const chatAssistantService = {
  /**
   * Create a new thread in LangGraph
   * Returns error tuple pattern
   */
  createThread: async (
    userId: string,
    metadata?: Record<string, any>,
    client?: LangGraphClient
  ): Promise<[Error | null, ThreadMetadata | null]> => {
    try {
      const c = client ?? langGraphClient;
      const thread = await c.createThread(metadata);

      const threadMetadata: ThreadMetadata = {
        id: thread.thread_id,
        langGraphThreadId: thread.thread_id,
        userId,
        title: metadata?.title || 'New conversation',
        lastMessageAt: Date.now(),
        messageCount: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      return [null, threadMetadata];
    } catch (error) {
      console.error('Failed to create thread:', error);
      return [error as Error, null];
    }
  },

  /**
   * Get thread history from LangGraph
   * Returns error tuple pattern with both messages and UI events
   */
  getThreadHistory: async (
    threadId: string,
    client?: LangGraphClient,
  ): Promise<[Error | null, ThreadHistoryData | null]> => {
    try {
      const c = client ?? langGraphClient;
      const state = await c.getThreadState(threadId);

      // Extract current team and team leader from onboarding_metadata
      const metadata = state.values.onboarding_metadata;
      let currentTeam: CurrentTeam | null = metadata?.current_team || null;
      const teamLeader: TeamLeader | null = metadata?.team_leader || null;

      // Fallback: if graph state doesn't have the team, fetch from the dedicated API endpoint
      // This is more reliable as it reads from the database directly
      if (!currentTeam) {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
        try {
          const teamResponse = await fetch(
            `${appConfig.API_BASE_URL}/agents/agent/threads/${threadId}/current-team`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            }
          );
          if (teamResponse.ok) {
            const teamData = await teamResponse.json();
            if (teamData?.id) {
              currentTeam = teamData;
            }
          }
        } catch {
          // Silently ignore — currentTeam stays null
        }
      }

      return [
        null,
        {
          messages: state.values.messages || [],
          uiEvents: state.values.ui || [],
          currentTeam,
          teamLeader,
        },
      ];
    } catch (error) {
      console.error('Failed to get thread history:', error);
      return [error as Error, null];
    }
  },

  /**
   * Send message with streaming response
   * Supports both string input (backward compatible) and message array for tool messages
   * Returns error tuple pattern
   */
  sendMessageStream: async (
    threadId: string,
    input: SendMessageInput,
    callbacks: StreamCallbacks,
    signal?: AbortSignal,
    client?: LangGraphClient
  ): Promise<[Error | null, StreamController | null]> => {
    try {
      const c = client ?? langGraphClient;

      // Convert input to message array format
      let messages: LangGraphMessageInput[];

      if (typeof input === 'string') {
        // Backward compatibility: string input becomes human message
        messages = [
          {
            type: 'human' as const,
            content: input,
          },
        ];
      } else {
        // New format: use message array directly
        messages = input;
      }

      const runInput = {
        input: {
          messages: messages,
        },
        streamMode: 'messages' as const,
      };

      const controller = await c.streamRun(
        threadId,
        runInput,
        callbacks,
        signal
      );

      return [null, controller];
    } catch (error) {
      console.error('Failed to send message stream:', error);
      return [error as Error, null];
    }
  },

  /**
   * Handle interrupt and resume run
   * Returns error tuple pattern
   */
  handleInterrupt: async (
    threadId: string,
    runId: string,
    response: any
  ): Promise<[Error | null, boolean]> => {
    try {
      await langGraphClient.resumeRun(threadId, runId, response);
      return [null, true];
    } catch (error) {
      console.error('Failed to handle interrupt:', error);
      return [error as Error, false];
    }
  },

  /**
   * Fetch the most recent threads for the assistant
   * Returns error tuple pattern
   */
  fetchRecentThreads: async (
    limit = 5,
    client?: LangGraphClient
  ): Promise<[Error | null, ThreadMetadata[] | null]> => {
    try {
      const c = client ?? langGraphClient;
      const rawThreads = await c.searchThreads({ limit });
      const threads: ThreadMetadata[] = rawThreads.map((t: any) => ({
        id: t.thread_id,
        langGraphThreadId: t.thread_id,
        userId: String(t.user_id ?? ''),
        title: t.title ?? 'Untitled conversation',
        lastMessageAt: t.updated_at ? new Date(t.updated_at).getTime() : Date.now(),
        messageCount: Array.isArray(t.messages) ? t.messages.length : 0,
        createdAt: t.created_at ? new Date(t.created_at).getTime() : Date.now(),
        updatedAt: t.updated_at ? new Date(t.updated_at).getTime() : Date.now(),
      }));
      return [null, threads];
    } catch (error) {
      return [error as Error, null];
    }
  },

  /**
   * Update the title of a thread
   * Returns error tuple pattern
   */
  updateThreadTitle: async (
    threadId: string,
    title: string
  ): Promise<[Error | null, boolean]> => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
      const response = await fetch(
        `${appConfig.API_BASE_URL}/agents/optimize-clean-up-agent/threads/${threadId}/title`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ title }),
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return [null, true];
    } catch (error) {
      console.error('Failed to update thread title:', error);
      return [error as Error, false];
    }
  },

  /**
   * Soft-delete a thread
   */
  deleteThread: async (
    threadId: string,
    client?: LangGraphClient
  ): Promise<[Error | null, boolean]> => {
    try {
      const c = client ?? langGraphClient;
      await c.deleteThread(threadId);
      return [null, true];
    } catch (error) {
      console.error('Failed to delete thread:', error);
      return [error as Error, false];
    }
  },

  /**
   * Get current team for a thread
   * Returns error tuple pattern
   */
  getCurrentTeam: async (
    threadId: string
  ): Promise<[Error | null, CurrentTeam | null]> => {
    try {
      const url = `${appConfig.API_BASE_URL}/agents/agent/threads/${threadId}/current-team`;
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return [null, data];
    } catch (error) {
      console.error('Failed to get current team:', error);
      return [error as Error, null];
    }
  },

};
